import React, { useState, useEffect } from 'react';
import {
  Modal, Input, Button, Typography, message, Tooltip, Tag, List
} from 'antd';
import {
  BugOutlined, VideoCameraOutlined, SendOutlined, PlusOutlined, 
  SettingOutlined, SyncOutlined, EyeOutlined, CheckCircleOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { API, Auth } from 'aws-amplify';
import dayjs from '../dayjs';
import config from '../config';
import {
  formatBytes,
  getIssueType,
  getStatusColor,
  getDeploymentStatusTooltip,
  fetchJiraTicketDetails
} from '../utils/bugReportUtils';
import { useSupportTicketCommentSystem } from '../features/Reportable/hooks/useSupportTicketCommentSystem';
import { useAppContext } from '../libs/contextLib';
import CommentSystemContent from '../features/Reportable/components/CommentSystemContent';
import JiraProgressBar from './JiraProgressBar';

const { Title, Text } = Typography;

// Set this to true to enable mock recording mode
const MOCK_RECORDING = false;

const BugReportModal = ({ 
  visible, 
  onClose, 
  user, 
  operator, 
  breadcrumbs, 
  isAdmin, 
  messageApi,
  recordingUrl: initialRecordingUrl,
  onRecordingComplete
}) => {
  // Get editor and user info from context
  const { editor, givenName, familyName, user: contextUser } = useAppContext();
  
  
  // Use support ticket system to load threads instead of old bug reports
  
  // Initialize support ticket comment system
  const supportTicketSystem = useSupportTicketCommentSystem(
    editor, 
    operator?.companyId, 
    operator?.operatorId
  );
  
  // Core state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [didRecord, setDidRecord] = useState(false);
  const [consoleLogs, setConsoleLogs] = useState([]);
  const [logFileUrl, setLogFileUrl] = useState('');
  const [mockRecordingInProgress, setMockRecordingInProgress] = useState(false);
  const [isProcessingRecording, setIsProcessingRecording] = useState(false);
  const [forceRegularMode, setForceRegularMode] = useState(false);
  const [showGuidanceModal, setShowGuidanceModal] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [activeCollapseKey, setActiveCollapseKey] = useState([]);
  
  
  // Issue details state
  const [selectedIssue, setSelectedIssue] = useState(null);
  
  
  // JIRA data state
  const [jiraData, setJiraData] = useState({});
  const [jiraDataLoading, setJiraDataLoading] = useState(false);
  const [jiraDataError, setJiraDataError] = useState(null);


  // Detect if running on localhost
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const isAdminOrDev = (isAdmin || isLocalhost) && !forceRegularMode;

  // Load support ticket threads on modal open
  useEffect(() => {
    if (visible && supportTicketSystem.loadSupportTicketThreads) {
      supportTicketSystem.loadSupportTicketThreads();
    }
  }, [visible]);


  // Load comments when an issue is selected
  useEffect(() => {
    if (selectedIssue && supportTicketSystem.loadCommentsForThread) {
      supportTicketSystem.loadCommentsForThread(selectedIssue.threadId);
    }
  }, [selectedIssue]);

  // Filter support ticket threads for current company and selected status
  const companyThreads = supportTicketSystem.availableThreads.filter(thread => 
    thread.companyId === operator?.companyId && !thread.archived
  );
  
  const filteredIssues = companyThreads
    .filter(thread => {
      if (selectedStatusFilter === 'all') return true;
      return thread.status?.toLowerCase() === selectedStatusFilter.toLowerCase();
    })
    .sort((a, b) => {
      // Sort by created date in descending order (newest first)
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateB - dateA;
    });

  // Check for duplicates (only log in development)
  const issueIds = filteredIssues.map(issue => issue.id || issue.bugId);
  const uniqueIds = [...new Set(issueIds)];
  if (issueIds.length !== uniqueIds.length && process.env.NODE_ENV === 'development') {
    console.warn('Duplicate issue IDs detected:', issueIds.filter((id, index) => issueIds.indexOf(id) !== index));
  }

  // Fetch JIRA data for tickets (simple, no real-time updates)
  const fetchJiraDataForTickets = async (tickets) => {
    if (!tickets || tickets.length === 0) return;
    
    // Prevent multiple simultaneous calls
    if (jiraDataLoading) return;
    
    const jiraKeys = tickets
      .map(ticket => ticket.metaInfo?.jiraId || ticket.metaInfo?.jiraKey)
      .filter(key => key && key.startsWith('BUS-'));
    
    if (jiraKeys.length === 0) return;
    
    setJiraDataLoading(true);
    setJiraDataError(null);
    
    try {
      const response = await API.post('third_party', '/jira/get-issue', {
        body: { issueKeys: jiraKeys }
      });
      
      // Parse the response body since it's a JSON string
      const responseData = typeof response.body === 'string' ? JSON.parse(response.body) : response;
      
      if (responseData.issues && responseData.issues.length > 0) {
        const jiraDataMap = {};
        responseData.issues.forEach(issue => {
          // JIRA Issue Data logging removed to reduce console spam
          
          jiraDataMap[issue.key] = {
            summary: issue.summary,
            status: issue.status,
            priority: issue.priority,
            updated: issue.updated,
            issueType: issue.issueType,
            deployedEnv: issue.deployedEnv,
            versions: issue.versions,
            created: issue.created
          };
        });
        
        setJiraData(jiraDataMap);
      }
    } catch (error) {
      console.error('Error fetching JIRA data:', error);
      setJiraDataError(error.message);
    } finally {
      setJiraDataLoading(false);
    }
  };

  // Load initial recording URL on mount
  useEffect(() => {
    if (visible) {
      // If we have an initial recording URL, set it
      if (initialRecordingUrl) {
        setRecordingUrl(initialRecordingUrl);
        setDidRecord(true);
      }
    }
  }, [visible, initialRecordingUrl]);

  // Fetch JIRA data when modal opens and when tickets change
  useEffect(() => {
    if (visible && companyThreads && companyThreads.length > 0 && !jiraDataLoading) {
      fetchJiraDataForTickets(companyThreads);
    }
  }, [visible, companyThreads.length]); // Only depend on length to prevent infinite loops

  // Refresh JIRA data when comments are added/updated to get latest timestamps
  useEffect(() => {
    if (visible && supportTicketSystem.comments && supportTicketSystem.comments.length > 0) {
      // Debounce the refresh to avoid too many API calls
      const timeoutId = setTimeout(() => {
        if (companyThreads && companyThreads.length > 0 && !jiraDataLoading) {
          fetchJiraDataForTickets(companyThreads);
        }
      }, 2000); // Wait 2 seconds after comment changes
      
      return () => clearTimeout(timeoutId);
    }
  }, [supportTicketSystem.comments?.length, visible]);


  // No need for manual loading - using global state with listeners

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!visible) {
      // Clear console logs when modal closes
      setConsoleLogs([]);
    }
  }, [visible]);

  // Mock recording completion effect
  useEffect(() => {
    if (visible && mockRecordingInProgress) {
      const timer = setTimeout(() => {
        console.log(`🎬 Mock recording completed`);
        
        setMockRecordingInProgress(false);
        const mockUrl = 'https://example.com/mock-recording';
        setRecordingUrl(mockUrl);
        
        messageApi.success(`Mock recording completed successfully!`);
        
        // Call the callback to reopen the modal with the recording URL
        if (onRecordingComplete) {
          onRecordingComplete(mockUrl);
        }
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [visible, mockRecordingInProgress, messageApi, onRecordingComplete]);

  // Start video recording
  const startRecording = () => {
    try {
      // Console capture is already started when modal opens
      console.log('🎬 Starting recording - console capture already active');
      
      if (MOCK_RECORDING) {
        // Mock recording mode
        setIsRecording(false);
        setMockRecordingInProgress(true);
        setDidRecord(true);
        // Clear previous logs when starting a new recording
        setConsoleLogs([]);
        setLogFileUrl('');

        // Close modal immediately for mock recording too
        onClose();
        // Show guidance modal instead of toast
        setShowGuidanceModal(true);
        
        // Add some test console logs to demonstrate capture
        console.log('Mock recording started - console capture active');
        console.info('This is a test info message for console capture');
        console.warn('This is a test warning message for console capture');
        console.error('This is a test error message for console capture');
        
        // Simulate some activity after a delay
        setTimeout(() => {
          console.log('Simulated user activity during mock recording');
          console.info('Console capture is working correctly');
        }, 2000);

        return;
      }

      // Real recording mode
      setIsRecording(true);
      setDidRecord(true);
      // Clear previous logs when starting a new recording
      setConsoleLogs([]);
      setLogFileUrl('');
      
      // Close modal immediately to prevent interference with Zight popup
      onClose();
      // Show guidance modal instead of toast
      setShowGuidanceModal(true);

      const options = {
        apiKey: config.zight.apiKey,
        onCompleted: function({ shareUrl, thumbnailUrl, embedUrl }) {
          console.log(`🎬 Recording completed`);
          
          // Store the share URL
          setRecordingUrl(shareUrl);
          setIsRecording(false);
          setIsProcessingRecording(false); // Processing complete
          setShowGuidanceModal(false); // Ensure guidance modal is closed
          
          messageApi.success(`Recording completed successfully!`);
          
          // Call the callback to reopen the modal with the recording URL
          if (onRecordingComplete) {
            onRecordingComplete(shareUrl);
          }
        },
        onStatusChange: function(newStatus) {
          if (newStatus === "recording") {
            // Recording has started
            setShowGuidanceModal(false); // Close guidance modal
            messageApi.info('Recording started. Please reproduce the issue.');
          } else if (newStatus === "stopped" || newStatus === "saved" || newStatus === "completed") {
            // Recording has been stopped/saved - show processing state
            setIsRecording(false);
            setIsProcessingRecording(true);
            messageApi.info('Processing your recording...');
          }
        },
        onError: function (error) {
          console.log(`❌ Recording failed`);
          
          messageApi.error('Recording failed: ' + error.message);
          console.error('Recording failed:', error);
          setIsRecording(false);
          setShowGuidanceModal(false); // Close guidance modal
        }
      };

      // Start the Zight recorder
      window.CloudAppRecorder.start(options);
      
      // Fallback timeout - if recording doesn't complete properly, check after 60 seconds
      setTimeout(() => {
        if (isRecording && !recordingUrl) {
          // If we're still in recording state but have no URL, something went wrong
          // This is a safety net
          setIsRecording(false);
          setIsProcessingRecording(false);
          setShowGuidanceModal(false);
          messageApi.warning('Recording may have completed. Please check and try again if needed.');
        }
      }, 60000);
    } catch (error) {
      messageApi.error('Failed to start recording: ' + error.message);
      console.error('Failed to start recording:', error);
      setIsRecording(false);
      setShowGuidanceModal(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Get first and last name directly - simple approach (DECLARE FIRST)
      const givenName = user?.attributes?.given_name;
      const familyName = user?.attributes?.family_name;
      const author = (givenName && familyName) ? `${givenName} ${familyName}` : user?.attributes?.email || 'Unknown User';
      
      setIsSubmitting(true);
      const currentUrl = window.location.href;
      
      // Declare uploadedLogFileUrl and uploadedLogFileKey at function scope
      let uploadedLogFileUrl = null;
      let uploadedLogFileKey = null;

      // Create support ticket thread IMMEDIATELY (before JIRA API call)
      let supportTicketThread = null;
      try {
        const ticketData = {
          title: title.trim() || `Support Ticket from ${user?.attributes?.email || 'Unknown User'}`,
          jiraId: null, // Will be updated if JIRA succeeds
          breadcrumbs: Array.isArray(breadcrumbs) ? breadcrumbs.map(b => 
            typeof b === 'string' ? b : JSON.stringify(b)
          ) : (breadcrumbs ? [breadcrumbs] : []),
          url: currentUrl,
          recordingUrl: recordingUrl,
          logFileUrl: logFileUrl || '' // Use state variable, fallback to empty string
        };
        
        supportTicketThread = await supportTicketSystem.handleCreateSupportTicketThread(ticketData);
        console.log('✅ Support ticket thread created:', supportTicketThread.threadId);
        
        // Add description as comment if provided
        if (description.trim()) {
          console.log('📝 Adding description comment:', description.trim());
          await supportTicketSystem.handleAddComment(supportTicketThread.threadId, description.trim());
          console.log('✅ Description comment added');
        } else {
          console.log('ℹ️ No description provided, skipping description comment');
        }
        
        // Add Zight video link as comment if available
        if (recordingUrl) {
          console.log('🎥 Adding video comment:', recordingUrl);
          const videoMessage = `📹 Screen Recording: [Click to watch](${recordingUrl})`;
          await supportTicketSystem.handleAddComment(supportTicketThread.threadId, videoMessage);
          console.log('✅ Video comment added');
        } else {
          console.log('ℹ️ No recording URL provided, skipping video comment');
        }
      } catch (threadError) {
        console.error('❌ Failed to create support ticket thread:', threadError);
        messageApi.error('Failed to create support ticket thread. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Admin/dev mode: skip recording/logs, just submit title, description, system info
      // if (isAdminOrDev) {

        // Create JIRA ticket with proper structure according to Atlassian API
        const ticketData = {
          fields: {
            project: {
              key: config.jira.projectKey
            },
            summary: title.trim() || `Support Ticket from ${user?.attributes?.email || 'Unknown User'} - [${dayjs().format('YYYY-MM-DD HH:mm:ss')}]`,
            description: {
              type: "doc",
              version: 1,
              content: [
                {
                  type: "paragraph",
                  content: [
                    {
                      type: "text",
                      text: `User: ${user?.attributes?.email || 'Unknown User'}`
                    }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    {
                      type: "text",
                      text: `Operator: ${operator?.operatorName || 'Unknown Operator'}`
                    }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    {
                      type: "text",
                      text: `URL: ${currentUrl}`
                    }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    {
                      type: "text",
                      text: `Issue: ${title.trim() || 'Support Ticket'}`
                    }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    {
                      type: "text",
                      text: `Description: ${description.trim() || 'No description provided'}`
                    }
                  ]
                },
                ...(recordingUrl ? [{
                  type: "paragraph",
                  content: [
                    {
                      type: "text",
                      text: "Recording: "
                    },
                    {
                      type: "inlineCard",
                      attrs: {
                        url: recordingUrl
                      }
                    }
                  ]
                }] : []),
                ...(uploadedLogFileUrl ? [{
                  type: "paragraph",
                  content: [
                    {
                      type: "text",
                      text: "Console Logs: "
                    },
                    {
                      type: "inlineCard",
                      attrs: {
                        url: uploadedLogFileUrl
                      }
                    }
                  ]
                }] : []),
                ...(uploadedLogFileKey ? [{
                  type: "paragraph",
                  content: [
                    {
                      type: "text",
                      text: `S3 Key: ${uploadedLogFileKey}`
                    }
                  ]
                }] : []),
              ]
            },
            issuetype: {
              name: getIssueType(isAdmin, isLocalhost)
            }
          }
        };

        // Check if JIRA ticket creation is enabled
        if (config.jira.enabled) {
          console.log('🎫 Creating JIRA ticket...');
          messageApi.info('Creating JIRA ticket...');
          
          let jiraId = null;
          try {
            if (!MOCK_RECORDING) {
              const response = await API.post('third_party', '/jira/create-issue', {
                body: ticketData
              });
              jiraId = response.key;
            } else {
              // For mock mode, create a fake issue key
              jiraId = `BUS-${Date.now()}`;
            }

            // Update support ticket thread with JIRA ID if successful
            if (supportTicketThread && jiraId) {
              await supportTicketSystem.handleUpdateSupportTicketThread(supportTicketThread.threadId, {
                metaInfo: {
                  ...supportTicketThread.metaInfo,
                  jiraId: jiraId
                }
              });
              console.log('✅ Support ticket thread updated with JIRA ID:', jiraId);
            }
          } catch (jiraError) {
            console.error('❌ JIRA API call failed:', jiraError);
            // Thread still exists, user can retry JIRA later
            messageApi.warning('Support ticket created but JIRA integration failed. You can retry later.');
          }
        } else {
          console.log('🚫 JIRA ticket creation disabled via config');
          messageApi.info('JIRA ticket creation disabled - focusing on S3 upload');
        }

        // Note: Bug report data is now stored in the support ticket thread
        // No need for separate saveBugReport() call - the thread contains all the data
        
        messageApi.success('Issue reported successfully!');
        
        // Clear all form state after successful submission
        setTitle('');
        setDescription('');
        setRecordingUrl('');
        setDidRecord(false);
        setConsoleLogs([]);
        setLogFileUrl('');
        
        // Refresh the bug reports list BEFORE closing modal so user can see it
        // No need to refresh - global state will update automatically via listeners
        onClose();
        
        setIsSubmitting(false);
        return;
      // }
      
    } catch (error) {
      console.error('Error in bug report submission:', error);
      messageApi.error('Failed to submit support ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Issue details handlers
  const handleIssueClick = (thread) => {
    setSelectedIssue(thread);
  };

  // Handle delete issue with confirmation and state cleanup
  const handleDeleteIssue = async (threadId) => {
    try {
      await supportTicketSystem.handleDeleteThread(threadId);
      // Clear selected issue if it was the one being deleted
      if (selectedIssue && selectedIssue.threadId === threadId) {
        setSelectedIssue(null);
      }
    } catch (error) {
      // Error message is already handled in handleDeleteThread
      console.error('Error deleting issue:', error);
    }
  };

  const handleOpenSupportDashboard = async () => {
    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();

      const url = new URL('https://support.busable.app/');
      url.searchParams.set('token', idToken);

      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch (error) {
      messageApi.error('Could not open Support Dashboard. Please try again.');
    }
  };

  return (
    <>
      {/* Main Bug Report Modal - OTR Issues Style */}
      <Modal
        title={
          <div style={{ 
            fontSize: '18px',
            fontWeight: '600',
            color: '#2d3748',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: '#e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <BugOutlined style={{ color: '#4a5568', fontSize: '16px' }} />
              </div>
              <span>Support Tickets</span>
            </div>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={handleOpenSupportDashboard}
              style={{
                height: '32px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                color: '#334155',
                fontWeight: '600'
              }}
            >
              Support Dashboard
            </Button>
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={null}
        width={800}
        style={{ 
          top: 20,
          borderRadius: '16px',
          overflow: 'hidden'
        }}
        styles={{
          body: {
            padding: '0',
            background: '#ffffff'
          },
          header: {
            background: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
            borderRadius: '0',
            padding: '20px 24px'
          }
        }}
      >
        <div style={{ 
          display: 'flex', 
          height: '600px',
          gap: '20px',
          padding: '24px'
        }}>
          {/* Left Container - Issues List */}
          <div style={{ 
            width: '240px',
            display: 'flex',
            flexDirection: 'column',
            background: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
          }}>
            {/* Issues Header */}
            <div style={{ 
              padding: '20px',
              borderBottom: '1px solid #e2e8f0',
              background: '#f8f9fa',
              position: 'relative'
            }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#2d3748',
                marginBottom: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  Tickets ({filteredIssues.length})
                </div>
                
                {/* Add New Issue Button */}
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    // Clear selected issue to show new issue form
                    setSelectedIssue(null);
                    // Reset recording state for new ticket
                    setDidRecord(false);
                    setRecordingUrl('');
                    setTitle('');
                    setDescription('');
                    setConsoleLogs([]);
                    setLogFileUrl('');
                  }}
                  className="primary-btn"
                  style={{ 
                    height: '24px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    borderRadius: '4px',
                    fontWeight: '500',
                    fontSize: '10px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                    color: 'white',
                    padding: '0 8px'
                  }}
                >
                  <PlusOutlined style={{ fontSize: '8px', color: 'white' }} />
                  New
                </Button>
              </div>
            </div>

            {/* Issues List */}
            <div style={{ 
              flex: 1,
              overflowY: 'auto',
              padding: '16px'
            }}>
              {supportTicketSystem.isLoading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                  Loading issues...
                </div>
              ) : filteredIssues.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                  No issues reported yet
                </div>
              ) : (
                filteredIssues.map((thread, index) => {
                  const isSelected = selectedIssue && selectedIssue.threadId === thread.threadId;
                  return (
                  <div
                      key={`support-ticket-${thread.threadId || thread.id || index}-${index}`}
                      onClick={() => handleIssueClick(thread)}
                    style={{
                      padding: '16px',
                      marginBottom: '12px',
                        background: isSelected ? '#f0f9ff' : '#ffffff',
                        border: isSelected ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                        boxShadow: isSelected ? '0 4px 12px rgba(59, 130, 246, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
                        transform: isSelected ? 'translateY(-1px)' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.target.style.background = '#f8fafc';
                          e.target.style.borderColor = '#cbd5e1';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.target.style.background = '#ffffff';
                          e.target.style.borderColor = '#e2e8f0';
                          e.target.style.transform = 'none';
                          e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                        }
                    }}
                  >
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: '600',
                      color: '#2d3748',
                      marginBottom: '8px',
                      lineHeight: '1.4',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {jiraData[thread.metaInfo?.jiraId]?.summary || thread.title || 'No title'}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginBottom: '4px',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 6px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '12px',
                        fontSize: '10px',
                        color: '#6b7280',
                        fontWeight: '500'
                      }}>
                        {thread.metaInfo?.jiraId || thread.threadId}
                      </div>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 6px',
                        backgroundColor: '#fef3c7',
                        color: '#d97706',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        MEDIUM
                      </div>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 6px',
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '500',
                        gap: '4px'
                      }}>
                        <MessageOutlined style={{ fontSize: '10px' }} />
                        {thread.commentCount || 0}
                      </div>
                    </div>
                    <div style={{ 
                      marginBottom: '8px'
                    }}>
                      <JiraProgressBar 
                        status={(() => {
                          const jiraStatus = jiraData[thread.metaInfo?.jiraId]?.status;
                          const threadStatus = thread.status;
                          const finalStatus = jiraStatus || threadStatus || 'Unknown';
                          
                          // Status Selection debug logging removed to reduce console spam
                          
                          return finalStatus;
                        })()}
                        jiraData={jiraData[thread.metaInfo?.jiraId] || {}}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#9ca3af',
                      fontWeight: '400',
                      marginBottom: '2px'
                    }}>
                      <span style={{ color: '#6b7280' }}>Created:</span> {new Date(thread.createdAt).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#9ca3af',
                      fontWeight: '400'
                    }}>
                      <span style={{ color: '#6b7280' }}>Updated:</span> {jiraData[thread.metaInfo?.jiraId]?.updated ? 
                        new Date(jiraData[thread.metaInfo?.jiraId].updated).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 
                        new Date(thread.updatedAt).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      }
                    </div>
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#9ca3af',
                      fontWeight: '500',
                      marginTop: '4px'
                    }}>
                      by {thread.author || 'Unknown'}
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right Container - New Issue Form or Issue Details */}
          <div style={{ 
            flex: 4,
            display: 'flex', 
            flexDirection: 'column', 
            background: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
          }}>
            {selectedIssue ? (
              // Issue Details View
              <>

                {/* Issue Details Content - Inline Comments */}
                <div style={{ 
                  flex: 1,
                  padding: '0',
                  overflowY: 'auto'
                }}>
                  <CommentSystemContent
                    isAdminOrDev={isAdminOrDev}
                    selectedTripForComment={selectedIssue}
                    comments={supportTicketSystem.comments || []}
                    newComment={supportTicketSystem.newComment}
                    setNewComment={supportTicketSystem.setNewComment}
                    isSavingComment={supportTicketSystem.isSavingComment}
                    commentGroupResolved={supportTicketSystem.commentGroupResolved}
                    isResolving={supportTicketSystem.isResolving}
                    isReopening={supportTicketSystem.isReopening}
                    showResolveModal={supportTicketSystem.showResolveModal}
                    setShowResolveModal={supportTicketSystem.setShowResolveModal}
                    selectedResolutionCode={supportTicketSystem.selectedResolutionCode}
                    setSelectedResolutionCode={supportTicketSystem.setSelectedResolutionCode}
                    customResolutionCode={supportTicketSystem.customResolutionCode}
                    setCustomResolutionCode={supportTicketSystem.setCustomResolutionCode}
                    resolutionCodes={supportTicketSystem.resolutionCodes}
                    setResolutionCodes={supportTicketSystem.setResolutionCodes}
                    currentThreadId={selectedIssue?.threadId}
                    availableThreads={supportTicketSystem.availableThreads}
                    issueTitles={supportTicketSystem.issueTitles}
                    jiraData={jiraData}
                    editingTitle={supportTicketSystem.editingTitle}
                    setEditingTitle={supportTicketSystem.setEditingTitle}
                    editingComment={supportTicketSystem.editingComment}
                    setEditingComment={supportTicketSystem.setEditingComment}
                    threadType="support_ticket"
                    resolutionComment={supportTicketSystem.resolutionComment}
                    deletingCommentId={supportTicketSystem.deletingCommentId}
                    onSaveComment={supportTicketSystem.handleAddComment}
                    onMarkAsResolved={supportTicketSystem.handleMarkAsResolved}
                    onReopenGroup={supportTicketSystem.handleReopenGroup}
                    onResolveWithCode={supportTicketSystem.handleResolveWithCode}
                    onAddResolutionCode={supportTicketSystem.handleAddResolutionCode}
                    onNewThread={supportTicketSystem.handleNewThread}
                    onSelectThread={supportTicketSystem.handleSelectThread}
                    onTitleEdit={supportTicketSystem.handleTitleEdit}
                    onTitleKeyPress={supportTicketSystem.handleTitleKeyPress}
                    onEditComment={supportTicketSystem.handleEditComment}
                    onSaveEditComment={supportTicketSystem.handleSaveEditComment}
                    onCancelEditComment={supportTicketSystem.handleCancelEditComment}
                    onDeleteComment={supportTicketSystem.handleDeleteComment}
                    onReopenCommentGroup={supportTicketSystem.handleReopenGroup}
                    editor={editor}
                    givenName={givenName}
                    familyName={familyName}
                    onDeleteThread={handleDeleteIssue}
                  />
                </div>
              </>
            ) : (
              // New Issue Form
              <>
            {/* Form Header */}
            <div style={{ 
              padding: '20px 24px',
              borderBottom: '1px solid #e2e8f0',
              background: '#f8f9fa',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '16px' 
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  background: '#e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#4a5568',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <BugOutlined />
                </div>
                <div>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: '#2d3748',
                    marginBottom: '4px'
                  }}>
                    Raise New Ticket
                  </div>
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#6b7280',
                    fontWeight: '500'
                  }}>
                    Raise a bug or enhancement directly to our engineers
                  </div>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <div style={{ 
              flex: 1,
              padding: '24px',
              overflowY: 'auto',
              background: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {/* Video Recording Section */}
              <div style={{
                padding: '20px',
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                borderRadius: '12px',
                border: '1px solid #bae6fd',
                textAlign: 'center'
              }}>
                <div style={{ 
                  fontSize: '32px', 
                  marginBottom: '12px',
                  filter: 'drop-shadow(0 4px 8px rgba(59, 130, 246, 0.2))'
                }}>
                  🎥
                </div>
                <h3 style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '18px', 
                  fontWeight: '700', 
                  color: '#1e40af',
                  letterSpacing: '-0.025em'
                }}>
                  Record Video <span style={{ color: '#ef4444' }}>*</span>
                </h3>
                <p style={{ 
                  margin: '0 0 16px 0', 
                  fontSize: '14px', 
                  color: '#1e40af',
                  lineHeight: '1.5'
                }}>
                  Show us exactly what's happening by recording your screen
                </p>
                
                {!didRecord ? (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      type="primary"
                      size="large"
                      onClick={startRecording}
                      loading={isRecording || mockRecordingInProgress}
                      style={{
                        height: '48px',
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        fontWeight: '600',
                        fontSize: '16px',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <VideoCameraOutlined />
                      {isRecording || mockRecordingInProgress ? 'Recording...' : 'Start Recording'}
                    </Button>
                  </div>
                ) : (
                  <div style={{
                    padding: '16px',
                    background: '#f0fdf4',
                    borderRadius: '8px',
                    border: '1px solid #bbf7d0'
                  }}>
                    <div style={{ 
                      fontSize: '16px', 
                      marginBottom: '8px', 
                      color: '#15803d',
                      fontWeight: '600'
                    }}>
                      ✅ Recording Complete
                    </div>
                    {recordingUrl && (
                      <a 
                        href={recordingUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          color: '#2563eb',
                          textDecoration: 'none',
                          fontSize: '14px'
                        }}
                      >
                        View Recording
                      </a>
                    )}
                  </div>
                )}
              </div>

              {/* Title Input */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151' 
                }}>
                  Issue Title <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <Input
                  placeholder="Brief description of the issue"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  style={{ 
                    borderRadius: '8px',
                    fontSize: '14px',
                    padding: '12px 16px',
                    border: '1px solid #e2e8f0',
                    transition: 'border-color 0.2s ease'
                  }}
                />
              </div>

              {/* Description Input */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151' 
                }}>
                  Description
                </label>
                <Input.TextArea
                  rows={4}
                  placeholder="Describe what you were trying to do, what went wrong, and any other details that might help us understand the issue..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{ 
                    borderRadius: '8px',
                    fontSize: '14px',
                    padding: '12px 16px',
                    border: '1px solid #e2e8f0',
                    transition: 'border-color 0.2s ease',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* System Information (Collapsible) */}
            </div>

            {/* Form Footer */}
            <div style={{ 
              padding: '20px 24px',
              borderTop: '1px solid #e2e8f0',
              background: '#f8f9fa',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <Button
                onClick={onClose}
                style={{
                  height: '40px',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  borderRadius: '8px',
                  fontWeight: '500',
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  color: '#374151',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={isSubmitting}
                onClick={handleSubmit}
                disabled={isSubmitting || isProcessingRecording || !didRecord || !title.trim()}
                style={{
                  height: '40px',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  background: (isProcessingRecording || !didRecord || !title.trim()) 
                    ? '#e5e7eb' 
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  boxShadow: (isProcessingRecording || !didRecord || !title.trim()) 
                    ? 'none' 
                    : '0 4px 12px rgba(102, 126, 234, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  color: (isProcessingRecording || !didRecord || !title.trim()) 
                    ? '#9ca3af' 
                    : '#ffffff'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting && !isProcessingRecording && didRecord && title.trim()) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isProcessingRecording && didRecord && title.trim()) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                  }
                }}
              >
                <SendOutlined />
                Submit
              </Button>
            </div>
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Guidance Modal - Points to Zight popup */}
      <Modal
        title={null}
        open={showGuidanceModal}
        footer={null}
        closable={false}
        centered
        width={500}
        styles={{
          body: { 
            textAlign: 'center',
            padding: '40px 24px'
          }
        }}
        maskClosable={false}
      >
        <div>
          {/* Large pointing hand emoji */}
          <div style={{
            fontSize: '120px',
            lineHeight: '1',
            marginBottom: '20px',
            transform: 'rotate(-45deg)',
            display: 'inline-block'
          }}>
            👉
          </div>
          
          <h2 style={{
            margin: '20px 0 16px 0',
            fontSize: '24px',
            fontWeight: '600',
            color: '#1e293b'
          }}>
            Look for the blue button!
          </h2>
          
          <p style={{
            fontSize: '18px',
            color: '#475569',
            margin: '0 0 24px 0',
            lineHeight: '1.5'
          }}>
            A blue button should appear in the <strong>top right corner</strong> of your browser.
          </p>
          
          <p style={{
            fontSize: '16px',
            color: '#64748b',
            margin: '0 0 32px 0'
          }}>
            Click on <strong>"Record Screen as video"</strong> to continue.
          </p>
          
          <Button
            onClick={() => {
              setShowGuidanceModal(false);
              setIsRecording(false);
              messageApi.info('No problem! You can try recording again or submit without a recording.');
            }}
            style={{
              height: '40px',
              paddingLeft: '24px',
              paddingRight: '24px',
              borderRadius: '6px'
            }}
          >
            I don't see it
          </Button>
        </div>
      </Modal>


    </>
  );
};

export default BugReportModal;
